import React, { useEffect } from 'react'

import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { ApplicationState } from '@store/types'
import { Content } from '@organisms/SemEmprestimo/styles'
import { ColorWrapper, TextParagraph, GridStyled, Separator } from '@atoms'
import { Colors } from '@utils/Colors'
import { Button } from '@interco/inter-ui/components/Button'
import Tag from '@utils/Tag'
import { Page } from '@templates'
import { validaEnvHomeByBridge } from '@utils/functions'
import IcHandMoney from '@interco/icons/core/human/ic_hand_money'
import { ConvenioKind, getConvenioKind, StepRefin } from '@utils/enums'
import { useTrackingRefin } from '@hooks'
import { BridgeService } from '@services/bridge'
import { OpportunityCard } from '@molecules'

const DESQUALIFICACAO_DADOS_1: Record<ConvenioKind, StepRefin> = {
  INSS: StepRefin.DESQUALIFICACAO_INSS_DADOS_1,
  SIAPE: StepRefin.DESQUALIFICACAO_SIAPE_DADOS_1,
  CTPS: StepRefin.DESQUALIFICACAO_CTPS_DADOS_1,
}

const DESQUALIFICACAO_DADOS_2: Record<ConvenioKind, StepRefin> = {
  INSS: StepRefin.DESQUALIFICACAO_INSS_DADOS_2,
  SIAPE: StepRefin.DESQUALIFICACAO_SIAPE_DADOS_2,
  CTPS: StepRefin.DESQUALIFICACAO_CTPS_DADOS_2,
}

const DESQUALIFICACAO_PROPOSTA: Record<ConvenioKind, StepRefin> = {
  INSS: StepRefin.DESQUALIFICACAO_INSS_PROPOSTA,
  SIAPE: StepRefin.DESQUALIFICACAO_SIAPE_PROPOSTA,
  CTPS: StepRefin.DESQUALIFICACAO_CTPS_PROPOSTA,
}

const DESQUALIFICACAO_OFERTA_SEGURO: Partial<Record<ConvenioKind, StepRefin>> = {
  INSS: StepRefin.DESQUALIFICACAO_INSS_OFERTA_SEGURO,
  SIAPE: StepRefin.DESQUALIFICACAO_SIAPE_OFERTA_SEGURO,
}

const Desqualificacao = () => {
  const { origemGoBack, isApp } = useSelector((state: ApplicationState) => state.ui.navigation)
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )
  const { step, content } = useLocation().state || {}

  function getStep() {
    const convenioKind = getConvenioKind(convenioCodigo)

    if (step === 'carregamento-dados-1') {
      return DESQUALIFICACAO_DADOS_1[convenioKind]
    }
    if (step === 'carregamento-dados-2') {
      return DESQUALIFICACAO_DADOS_2[convenioKind]
    }
    if (step === 'oferta-seguro') {
      return DESQUALIFICACAO_OFERTA_SEGURO[convenioKind] ?? StepRefin.DESQUALIFICACAO
    }
    if (step === 'iniciar-simular') {
      return StepRefin.DESQUALIFICACAO_SIAPE_SIMULAR
    }
    if (step === 'desqualificacao-proposta') {
      return DESQUALIFICACAO_PROPOSTA[convenioKind]
    }

    return StepRefin.DESQUALIFICACAO
  }

  const getStepRefin = getStep()
  useTrackingRefin(getStepRefin)

  useEffect(() => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:feedback',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Rejeição',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Rejeição',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:feedback',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
      })
    }
  }, [isApp])

  const redirectGoBack = () => validaEnvHomeByBridge(String(origemGoBack), isApp) || ''

  const handleOpenFgts = () => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:feedback',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Conheça outras opções',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Conheça outras opções',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:feedback',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
      })
    }
    const deeplink = String(process.env.REACT_APP_FGTS_DEEPLINK || '')
    const site = String(process.env.REACT_APP_FGTS_SITE || '')

    if (!isApp) {
      if (site) {
        window.location.href = site
      }
      return
    }

    if (deeplink) {
      BridgeService.openDeepLink(deeplink)
    }
  }

  const handleOpenInter = () => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:feedback',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Conheça outras opções',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Conheça outras opções',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:feedback',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'rejection',
      })
    }
    window.location.href = String(process.env.REACT_APP_CONTA_DIGITAL_NC || '')
  }

  return (
    <Page
      showLeftIcon={false}
      stickyFooter={
        <Button
          variant="link"
          onClick={() => {
            window.location.href = redirectGoBack()
          }}
        >
          Voltar para home
        </Button>
      }
    >
      <Content>
        <GridStyled margin="0">
          <div style={{ padding: '16px', borderRadius: '60px', background: Colors.GRAY100 }}>
            <IcHandMoney width={32} height={32} color={Colors.GRAY500} />
          </div>
        </GridStyled>
        <ColorWrapper fontWeight="600" margin="24px 0 8px 0" style={{ textAlign: 'center' }}>
          <TextParagraph variant="headline-h1">
            Ainda não conseguimos te oferecer esse crédito
          </TextParagraph>
        </ColorWrapper>

        {content === 'FGTS' ? (
          <>
            <Separator variant="small" margin="8px 0 24px 0" />
            <ColorWrapper
              fontWeight="700"
              margin="24px 0 8px 0"
              style={{ marginBottom: '24px', alignSelf: 'flex-start' }}
            >
              <TextParagraph variant="headline-h2">Conheça outras opções</TextParagraph>
            </ColorWrapper>
            <OpportunityCard
              subtitle="Antecipação FGTS"
              description="Antecipe até 15 anos com ótimas taxas!"
              ariaLabel="Oportunidade: Antecipação FGTS"
              onClick={() => handleOpenFgts()}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleOpenFgts()
                }
              }}
            />
          </>
        ) : (
          <></>
        )}

        {content === 'ACCOUNT' ? (
          <>
            <Separator variant="small" margin="8px 0 24px 0" />
            <ColorWrapper
              fontWeight="700"
              margin="24px 0 8px 0"
              style={{ marginBottom: '24px', alignSelf: 'flex-start' }}
            >
              <TextParagraph variant="headline-h2">Conheça outras opções</TextParagraph>
            </ColorWrapper>
            <OpportunityCard
              subtitle="Abra sua conta no Inter"
              description="Tenha acesso a toda plataforma do Inter gratuitamente!"
              ariaLabel="Oportunidade: Abra sua conta no Inter"
              onClick={() => handleOpenInter()}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleOpenInter()
                }
              }}
            />
          </>
        ) : (
          <></>
        )}
      </Content>
    </Page>
  )
}
export default Desqualificacao
